<script setup>
import { ref, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import ReportMetricCard from './ReportMetricCard.vue';
import ReportsAPI from 'dashboard/api/reports';

const props = defineProps({
  filters: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();
const latestRequestId = ref(0);

const conversationCount = ref('0');
const messageCount = ref('0');
const resolutionRate = ref('0');
const handoffRate = ref('0');

const aiCloserConversationCount = ref('0');
const aiGamerConversationCount = ref('0');
const aiCloserResolutionRate = ref('0');
const aiGamerResolutionRate = ref('0');
const aiCloserHandoffRate = ref('0');
const aiGamerHandoffRate = ref('0');

const toSafeNumber = value => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : 0;
};

const resetMetrics = () => {
  conversationCount.value = '0';
  messageCount.value = '0';
  resolutionRate.value = '0';
  handoffRate.value = '0';
  aiCloserConversationCount.value = '0';
  aiGamerConversationCount.value = '0';
  aiCloserResolutionRate.value = '0';
  aiGamerResolutionRate.value = '0';
  aiCloserHandoffRate.value = '0';
  aiGamerHandoffRate.value = '0';
};

const formatToPercent = value => {
  return value ? `${value}%` : '--';
};

const fetchMetrics = async () => {
  if (!props.filters.to || !props.filters.from) {
    return;
  }
  const requestId = latestRequestId.value + 1;
  latestRequestId.value = requestId;

  try {
    const response = await ReportsAPI.getBotMetrics(props.filters);
    if (requestId !== latestRequestId.value) return;

    const data = response?.data || {};
    conversationCount.value = toSafeNumber(
      data.conversation_count
    ).toLocaleString();
    messageCount.value = toSafeNumber(data.message_count).toLocaleString();
    resolutionRate.value = toSafeNumber(data.resolution_rate).toString();
    handoffRate.value = toSafeNumber(data.handoff_rate).toString();

    aiCloserConversationCount.value = toSafeNumber(
      data.ai_closer_conversation_count
    ).toLocaleString();
    aiGamerConversationCount.value = toSafeNumber(
      data.ai_gamer_conversation_count
    ).toLocaleString();
    aiCloserResolutionRate.value = toSafeNumber(
      data.ai_closer_resolution_rate
    ).toString();
    aiGamerResolutionRate.value = toSafeNumber(
      data.ai_gamer_resolution_rate
    ).toString();
    aiCloserHandoffRate.value = toSafeNumber(
      data.ai_closer_handoff_rate
    ).toString();
    aiGamerHandoffRate.value = toSafeNumber(
      data.ai_gamer_handoff_rate
    ).toString();
  } catch {
    if (requestId !== latestRequestId.value) return;
    resetMetrics();
    useAlert(t('REPORT.DATA_FETCHING_FAILED'));
  }
};

watch(() => props.filters, fetchMetrics, { deep: true });

onMounted(fetchMetrics);
</script>

<template>
  <div class="flex flex-col gap-3">
    <!-- Overall WLAB AI -->
    <div
      class="flex flex-wrap mx-0 shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2 px-6 py-5"
    >
      <ReportMetricCard
        :label="$t('BOT_REPORTS.METRIC.TOTAL_CONVERSATIONS.LABEL')"
        :info-text="$t('BOT_REPORTS.METRIC.TOTAL_CONVERSATIONS.TOOLTIP')"
        :value="conversationCount"
        class="flex-1"
      />
      <ReportMetricCard
        :label="$t('BOT_REPORTS.METRIC.TOTAL_RESPONSES.LABEL')"
        :info-text="$t('BOT_REPORTS.METRIC.TOTAL_RESPONSES.TOOLTIP')"
        :value="messageCount"
        class="flex-1"
      />
      <ReportMetricCard
        :label="$t('BOT_REPORTS.METRIC.RESOLUTION_RATE.LABEL')"
        :info-text="$t('BOT_REPORTS.METRIC.RESOLUTION_RATE.TOOLTIP')"
        :value="formatToPercent(resolutionRate)"
        class="flex-1"
      />
      <ReportMetricCard
        :label="$t('BOT_REPORTS.METRIC.HANDOFF_RATE.LABEL')"
        :info-text="$t('BOT_REPORTS.METRIC.HANDOFF_RATE.TOOLTIP')"
        :value="formatToPercent(handoffRate)"
        class="flex-1"
      />
    </div>

    <!-- Breakdown -->
    <div
      class="flex flex-wrap mx-0 shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2 px-6 py-5"
    >
      <ReportMetricCard
        :label="$t('BOT_REPORTS.METRIC.AI_CLOSER_CONVERSATIONS.LABEL')"
        :info-text="$t('BOT_REPORTS.METRIC.AI_CLOSER_CONVERSATIONS.TOOLTIP')"
        :value="aiCloserConversationCount"
        class="flex-1"
      />
      <ReportMetricCard
        :label="$t('BOT_REPORTS.METRIC.AI_GAMER_CONVERSATIONS.LABEL')"
        :info-text="$t('BOT_REPORTS.METRIC.AI_GAMER_CONVERSATIONS.TOOLTIP')"
        :value="aiGamerConversationCount"
        class="flex-1"
      />
      <ReportMetricCard
        :label="$t('BOT_REPORTS.METRIC.AI_CLOSER_HANDOFF_RATE.LABEL')"
        :info-text="$t('BOT_REPORTS.METRIC.AI_CLOSER_HANDOFF_RATE.TOOLTIP')"
        :value="formatToPercent(aiCloserHandoffRate)"
        class="flex-1"
      />
      <ReportMetricCard
        :label="$t('BOT_REPORTS.METRIC.AI_GAMER_HANDOFF_RATE.LABEL')"
        :info-text="$t('BOT_REPORTS.METRIC.AI_GAMER_HANDOFF_RATE.TOOLTIP')"
        :value="formatToPercent(aiGamerHandoffRate)"
        class="flex-1"
      />
    </div>
  </div>
</template>
